/**
 * ATT-3384 admin half — fetch the override TARGET tenant's branding by host.
 *
 * Restores the ATT-3226 staging tenant-override branding simulation that ATT-3383
 * safe-degraded to dormant. The backend `tenantBrandingByHost(host, portal)` query
 * (api#1039) resolves the tenant by the `host` ARGUMENT and is public +
 * host-agnostic (the tenant-resolver middleware is exempted for this one
 * operation, so it needs NO `X-Tenant-Domain` header — "Option B").
 *
 * CRITICAL — this queries PRODUCTION, not the resolved staging backend. The 12
 * tenants' branding lives in the PROD registry; the staging registry only holds
 * the `staging` tenant. So we use a DEDICATED `fetch` to
 * `NEXT_PUBLIC_BRANDING_API_BASE` (default `https://api.attendly.com`) — NOT the
 * shared `graphqlClient` (which targets the resolved staging `apiUrl` and injects
 * the real-host header). Branding is public + non-sensitive; the cross-origin
 * call to prod carries no credentials and no tenant header. Auth/data
 * (`apiUrl`/`cookieDomain`) still point at the real staging backend (Option A).
 *
 * Returns the parsed branding, or null on an unknown/inactive host, a transport
 * error, GraphQL errors, or a malformed response (Sentry-instrumented).
 */

import * as Sentry from "@sentry/nextjs";
import { z } from "zod";

/** Which portal's branding to resolve. frontend-admin always passes "admin". */
export type TenantPortal = "admin" | "user";

const TenantBrandingSchema = z.object({
  displayName: z.string().nullable(),
  logoUrl: z.string().nullable(),
  featureFlags: z.array(z.string()),
  hideLanguages: z.array(z.string()),
});

export type TenantBranding = z.infer<typeof TenantBrandingSchema>;

const TENANT_BRANDING_BY_HOST_QUERY = `
  query TenantBrandingByHost($host: String!, $portal: TenantPortal!) {
    tenantBrandingByHost(host: $host, portal: $portal) {
      displayName
      logoUrl
      featureFlags
      hideLanguages
    }
  }
`;

/**
 * Production GraphQL base for the public branding query. Overridable via env for
 * non-prod testing; defaults to prod because that is where the full tenant
 * registry (all 12 tenants' branding) lives.
 */
const BRANDING_API_BASE =
  process.env.NEXT_PUBLIC_BRANDING_API_BASE ?? "https://api.attendly.com";

function captureMalformed(host: string, portal: TenantPortal, detail: unknown): void {
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("event", "tenant-branding-by-host-malformed");
    scope.setExtras({ host, portal, detail });
    Sentry.captureMessage("tenant-branding-by-host-malformed", { level: "warning" });
  });
}

export async function fetchTenantBrandingByHost(
  host: string,
  portal: TenantPortal,
): Promise<TenantBranding | null> {
  let json: { data?: { tenantBrandingByHost?: unknown }; errors?: unknown };
  try {
    const res = await fetch(`${BRANDING_API_BASE}/graphql`, {
      method: "POST",
      // Public, host-agnostic query — NO X-Tenant-Domain, NO credentials.
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: TENANT_BRANDING_BY_HOST_QUERY,
        variables: { host, portal },
      }),
    });
    if (!res.ok) {
      captureMalformed(host, portal, { httpStatus: res.status });
      return null;
    }
    json = await res.json();
  } catch (err) {
    Sentry.captureException(err, {
      tags: { event: "tenant-branding-by-host-failed" },
    });
    return null;
  }

  if (json?.errors) {
    captureMalformed(host, portal, { graphqlErrors: json.errors });
    return null;
  }

  const node = json?.data?.tenantBrandingByHost;
  if (node == null) return null; // unknown / inactive host — graceful, no override branding

  const parsed = TenantBrandingSchema.safeParse(node);
  if (!parsed.success) {
    captureMalformed(host, portal, parsed.error.issues.map((i) => ({ path: i.path, message: i.message })));
    return null;
  }

  return parsed.data;
}
